import { useState, useEffect, useRef, useMemo } from 'react';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import toast from 'react-hot-toast';
import { whatsappImportApi, ImportedContact, GroupInfo, WhatsAppLabel } from '../services/whatsappImportApi';
import { interactiveCampaignApi, Connection } from '../services/interactiveCampaignApi';
import { api } from '../services/api';
import { Category } from '../types';
import { ScrollableSelect } from '../components/ScrollableSelect';

const CONTACTS_PER_PAGE = 20;

export function WhatsAppImportPage() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Opções de importação
  const [importContacts, setImportContacts] = useState(false);
  const [importGroups, setImportGroups] = useState(false);
  const [importByLabels, setImportByLabels] = useState(false);

  // Dados importados
  const [contacts, setContacts] = useState<ImportedContact[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [allImportedContacts, setAllImportedContacts] = useState<ImportedContact[]>([]);

  // Seleção de contatos
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);

  // Layout helpers
  const leftColumnRef = useRef<HTMLDivElement | null>(null);
  const [groupsPanelHeight, setGroupsPanelHeight] = useState<number | null>(null);

  // Labels (etiquetas) do WhatsApp Business
  const [whatsappLabels, setWhatsappLabels] = useState<WhatsAppLabel[]>([]);
  const [selectedLabelId, setSelectedLabelId] = useState<string>('');
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [labelContactsCount, setLabelContactsCount] = useState<Record<string, number>>({});

  // Categorias para importação
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Modal de importação
  const [showImportModal, setShowImportModal] = useState(false);

  const importCategoryOptions = useMemo(
    () => [
      { value: '', label: 'Sem categoria' },
      ...categories
        .filter((cat) => cat.nome.trim().toLowerCase() !== 'sem categoria')
        .map((cat) => ({ value: cat.id, label: cat.nome })),
    ],
    [categories]
  );

  useEffect(() => {
    loadConnections();
    loadCategories();
  }, []);

  // Carregar labels quando conexão WAHA for selecionada
  useEffect(() => {
    const loadLabels = async () => {
      if (!selectedConnection) {
        setWhatsappLabels([]);
        setSelectedLabelId('');
        return;
      }

      const conn = connections.find(c => c.id === selectedConnection);
      if (!conn || conn.provider !== 'WAHA') {
        setWhatsappLabels([]);
        setSelectedLabelId('');
        return;
      }

      try {
        setLoadingLabels(true);
        const labels = await whatsappImportApi.getLabels(conn.instanceName);
        setWhatsappLabels(labels || []);
      } catch (error: any) {
        console.error('Erro ao carregar labels:', error);
        setWhatsappLabels([]);
      } finally {
        setLoadingLabels(false);
      }
    };

    loadLabels();
  }, [selectedConnection, connections]);

  // Sempre que o total de contatos mudar, voltar para a primeira página
  useEffect(() => {
    setCurrentPage(1);
  }, [allImportedContacts.length]);

  // Sincronizar altura do painel de grupos com a coluna da esquerda (até o botão "Buscar dados")
  useEffect(() => {
    const updateHeight = () => {
      if (leftColumnRef.current) {
        setGroupsPanelHeight(leftColumnRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const loadConnections = async () => {
    try {
      // Helper para fazer requisições autenticadas
      const token = localStorage.getItem('auth_token');
      const tenantId = localStorage.getItem('selected_tenant_id') || localStorage.getItem('currentTenantId') || '';
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }

      if (tenantId) {
        (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
      }

      // Buscar sessões WhatsApp ao invés de connections
      const response = await fetch('/api/waha/sessions', {
        headers,
      });

      if (!response.ok) throw new Error('Erro ao carregar sessões');

      const data = await response.json();

      // Mapear sessões para formato de Connection
      const mappedConnections = (data || []).map((session: any) => ({
        id: session.sessionId || session.id || session.name,
        provider: (session.provider || 'WAHA') as 'WAHA' | 'EVOLUTION' | 'QUEPASA',
        instanceName: session.name || session.displayName,
        phoneNumber: session.meId?.replace(/@c\.us$/, '') || '',
        status: (session.status === 'WORKING' ? 'WORKING' : 'INACTIVE') as 'ACTIVE' | 'INACTIVE' | 'ERROR',
        callbackUrl: '',
        webhookSecret: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Connection));

      const filteredConnections = mappedConnections.filter((c: any) => c.status === 'WORKING');
      setConnections(filteredConnections);
    } catch {
      toast.error('Erro ao carregar conexões WhatsApp');
    }
  };

  const loadCategories = async () => {
    try {
      const allCategories = await api.getAllCategories();
      setCategories(allCategories || []);
    } catch {
      // falha silenciosa ao carregar categorias
    }
  };


  const handleFetchData = async () => {
    if (!selectedConnection) {
      toast.error('Selecione uma conexão WhatsApp');
      return;
    }

    setLoading(true);
    setContacts([]);
    setGroups([]);
    setAllImportedContacts([]);
    setSelectedContacts([]);
    setLabelContactsCount({});

    try {
      const conn = connections.find(c => c.id === selectedConnection);
      if (!conn) return;

      let allContacts: ImportedContact[] = [];

      // Buscar contatos salvos
      if (importContacts) {
        toast.loading('Buscando contatos salvos...', { id: 'fetch-contacts' });
        const fetchedContacts = await whatsappImportApi.getContacts(conn.instanceName);
        setContacts(fetchedContacts);
        allContacts = [...allContacts, ...fetchedContacts];
        toast.success(`${fetchedContacts.length} contatos encontrados`, { id: 'fetch-contacts' });
      }

      // Buscar grupos
      if (importGroups) {
        toast.loading('Buscando grupos...', { id: 'fetch-groups' });
        const fetchedGroups = await whatsappImportApi.getGroups(conn.instanceName);
        setGroups(fetchedGroups);
        toast.success(`${fetchedGroups.length} grupos encontrados`, { id: 'fetch-groups' });
      }

      // Para etiquetas, apenas carregar a lista (os contatos serão buscados individualmente)
      if (importByLabels && conn.provider === 'WAHA') {
        // Labels já são carregadas automaticamente pelo useEffect
        toast.success('Etiquetas carregadas. Clique em "Buscar Contatos" em cada etiqueta.', { id: 'fetch-labels' });
      }

      setAllImportedContacts(allContacts);
      if (allContacts.length > 0 || (importByLabels && conn.provider === 'WAHA')) {
        toast.success('Dados carregados com sucesso!', { id: 'fetch' });
      }
    } catch (error: any) {
      console.error('Erro ao buscar dados:', error);
      toast.error(error.response?.data?.error || 'Erro ao buscar dados do WhatsApp', { id: 'fetch' });
    } finally {
      setLoading(false);
    }
  };

  const handleFetchGroupParticipants = async (groupId: string, groupName: string) => {
    const conn = connections.find(c => c.id === selectedConnection);
    if (!conn) return;

    try {
      toast.loading(`Buscando participantes de "${groupName}"...`, { id: `group-${groupId}` });
      const participants = await whatsappImportApi.getGroupParticipants(conn.instanceName, groupId, groupName);

      // Adicionar participantes à lista geral
      setAllImportedContacts(prev => [...prev, ...participants]);
      toast.success(`${participants.length} participantes adicionados`, { id: `group-${groupId}` });
    } catch (error: any) {
      console.error('Erro ao buscar participantes:', error);
      toast.error('Erro ao buscar participantes do grupo', { id: `group-${groupId}` });
    }
  };

  const handleFetchLabelContacts = async (labelId: string, labelName: string) => {
    const conn = connections.find(c => c.id === selectedConnection);
    if (!conn) return;

    try {
      toast.loading(`Buscando contatos da etiqueta "${labelName}"...`, { id: `label-${labelId}` });

      // Buscar a cor da label
      const label = whatsappLabels.find(l => l.id === labelId);
      const labelColor = label?.colorHex;

      const contacts = await whatsappImportApi.getContactsByLabel(conn.instanceName, labelId, labelName);

      // Adicionar cor da label aos contatos
      const contactsWithColor = contacts.map(c => ({
        ...c,
        labelColor: labelColor || undefined,
      }));

      // Adicionar contatos à lista geral, evitando duplicatas por ID
      setAllImportedContacts(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newContacts = contactsWithColor.filter(c => !existingIds.has(c.id));
        return [...prev, ...newContacts];
      });
      
      setLabelContactsCount(prev => ({ ...prev, [labelId]: contacts.length }));
      toast.success(`${contacts.length} contatos encontrados na etiqueta`, { id: `label-${labelId}` });
    } catch (error: any) {
      console.error('Erro ao buscar contatos da etiqueta:', error);
      toast.error(error.response?.data?.error || 'Erro ao buscar contatos da etiqueta', { id: `label-${labelId}` });
    }
  };

  const handleSelectAll = (pageContacts: ImportedContact[]) => {
    const pageIds = pageContacts.map(c => c.id);
    const allSelectedOnPage = pageIds.every(id => selectedContacts.includes(id));

    if (allSelectedOnPage) {
      setSelectedContacts(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedContacts(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleSelectAllGlobal = () => {
    if (selectedContacts.length === allImportedContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(allImportedContacts.map(c => c.id));
    }
  };

  const handleToggleContact = (id: string) => {
    setSelectedContacts(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleImportClick = () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione ao menos um contato para importar');
      return;
    }
    setShowImportModal(true);
  };

  const handleConfirmImport = async () => {
    const contactsToImport = allImportedContacts.filter(c => selectedContacts.includes(c.id));

    try {
      setShowImportModal(false);
      toast.loading(`Importando ${contactsToImport.length} contatos...`, { id: 'import' });
      const result = await whatsappImportApi.importContacts(contactsToImport, selectedCategory || undefined);

      toast.success(
        `✅ ${result.imported} importados | ⚠️ ${result.duplicates} duplicados | ❌ ${result.errors} erros`,
        { id: 'import', duration: 5000 }
      );

      // Limpar seleção
      setSelectedContacts([]);
      setSelectedCategory('');
    } catch (error: any) {
      console.error('Erro ao importar contatos:', error);
      toast.error('Erro ao importar contatos', { id: 'import' });
    }
  };

  const handleExportCSV = () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione ao menos um contato para exportar');
      return;
    }

    const contactsToExport = allImportedContacts
      .filter(c => selectedContacts.includes(c.id))
      .map(c => ({
        nome: c.name,
        telefone: c.phone,
        email: '', // Contatos do WhatsApp não têm email
        observacoes: c.groupName 
          ? `Origem: ${c.source === 'contact' ? 'Contato' : c.source === 'group' ? 'Grupo' : c.source === 'label' ? 'Etiqueta WhatsApp' : 'Conversa'}${c.groupName ? ` - Grupo: ${c.groupName}` : ''}` 
          : `Origem: ${c.source === 'contact' ? 'Contato' : c.source === 'group' ? 'Grupo' : c.source === 'label' ? 'Etiqueta WhatsApp' : 'Conversa'}`,
        categoriaId: '', // Deixar vazio para o usuário preencher se necessário
      }));

    // Criar CSV no formato do template: nome,telefone,email,observacoes,categoriaId
    const csvHeaders = 'nome,telefone,email,observacoes,categoriaId\n';
    const csvRows = contactsToExport
      .map(c => {
        // Escapar vírgulas e aspas nos valores
        const escapeCSV = (value: string) => {
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        };
        return `${escapeCSV(c.nome)},${escapeCSV(c.telefone)},${escapeCSV(c.email)},${escapeCSV(c.observacoes)},${escapeCSV(c.categoriaId)}`;
      })
      .join('\n');

    const csvContent = csvHeaders + csvRows;

    // Criar blob e fazer download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM para Excel reconhecer UTF-8
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contatos_whatsapp_${new Date().getTime()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success(`${contactsToExport.length} contatos exportados para CSV`);
  };

  // Paginação de contatos encontrados
  const totalContacts = allImportedContacts.length;
  const totalPages = Math.max(1, Math.ceil(totalContacts / CONTACTS_PER_PAGE));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const startIndex = (currentPageSafe - 1) * CONTACTS_PER_PAGE;
  const paginatedContacts = allImportedContacts.slice(
    startIndex,
    startIndex + CONTACTS_PER_PAGE
  );
  const selectedOnPageCount = paginatedContacts.filter(c => selectedContacts.includes(c.id)).length;
  const allSelectedOnPage =
    paginatedContacts.length > 0 && selectedOnPageCount === paginatedContacts.length;

  return (
    <>
      <Header
        title="Extrair Contatos"
        subtitle="Busque contatos salvos e participantes de grupos das suas conexões WhatsApp"
        compactTopbar
      />
      <PageLayout>
        <PageHeader
          title="Extrair Contatos"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Contatos', href: '/contatos' },
            { label: 'Extrair Contatos' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            {/* Cabeçalho do card */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Extrair Contatos</h2>
            </div>

            <div className="p-5 sm:p-6 space-y-6">
              {/* Box de instruções em verde */}
              <div className="rounded-xl border p-4 border-emerald-500 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/15">
                <div className="flex items-start gap-3">
                  <div className="-mt-0.5 text-emerald-500">
                    <svg className="fill-current" width="24" height="24" viewBox="0 0 24 24">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M3.70186 12.0001C3.70186 7.41711 7.41711 3.70186 12.0001 3.70186C16.5831 3.70186 20.2984 7.41711 20.2984 12.0001C20.2984 16.5831 16.5831 20.2984 12.0001 20.2984C7.41711 20.2984 3.70186 16.5831 3.70186 12.0001ZM12.0001 1.90186C6.423 1.90186 1.90186 6.423 1.90186 12.0001C1.90186 17.5772 6.423 22.0984 12.0001 22.0984C17.5772 22.0984 22.0984 17.5772 22.0984 12.0001C22.0984 6.423 17.5772 1.90186 12.0001 1.90186ZM15.6197 10.7395C15.9712 10.388 15.9712 9.81819 15.6197 9.46672C15.2683 9.11525 14.6984 9.11525 14.347 9.46672L11.1894 12.6243L9.6533 11.0883C9.30183 10.7368 8.73198 10.7368 8.38051 11.0883C8.02904 11.4397 8.02904 12.0096 8.38051 12.3611L10.553 14.5335C10.7217 14.7023 10.9507 14.7971 11.1894 14.7971C11.428 14.7971 11.657 14.7023 11.8257 14.5335L15.6197 10.7395Z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                      Como funciona a extração
                    </h4>
                    <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                      <li>• Escolha uma conexão WhatsApp ativa.</li>
                      <li>• Marque o que deseja buscar: contatos salvos, grupos e/ou etiquetas.</li>
                      <li>• Clique em <strong>Buscar dados</strong> para carregar as informações.</li>
                      <li>• Use os botões em Etiquetas e Grupos para trazer mais contatos.</li>
                      <li>• Selecione os contatos encontrados e escolha Importar para o sistema ou Exportar para CSV.</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Grid 2 colunas: esquerda filtros, direita informações encontradas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* Coluna esquerda: conexão + o que buscar */}
                <div ref={leftColumnRef} className="space-y-6">
                  {/* Seleção de Conexão */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                      1. Escolha a conexão
                    </h3>
                    <select
                      value={selectedConnection}
                      onChange={(e) => setSelectedConnection(e.target.value)}
                      className="input-field w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 rounded-xl"
                    >
                      <option value="">Selecione uma conexão WhatsApp...</option>
                      {connections.map((conn) => (
                        <option key={conn.id} value={conn.id}>
                          {conn.instanceName} - {conn.phoneNumber}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* O que buscar */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                      2. O que você quer buscar?
                    </h3>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importContacts}
                        onChange={(e) => setImportContacts(e.target.checked)}
                        className="w-5 h-5 text-emerald-600 border-gray-300 dark:border-gray-600 rounded focus:ring-emerald-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white/90">
                          Contatos salvos
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Contatos já salvos na agenda do WhatsApp.
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importGroups}
                        onChange={(e) => setImportGroups(e.target.checked)}
                        className="w-5 h-5 text-emerald-600 border-gray-300 dark:border-gray-600 rounded focus:ring-emerald-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white/90">
                          Grupos
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Trazer participantes dos grupos em que a conexão está.
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importByLabels}
                        onChange={(e) => {
                          setImportByLabels(e.target.checked);
                          if (e.target.checked) {
                            setImportContacts(false);
                            setImportGroups(false);
                          }
                        }}
                        className="w-5 h-5 text-emerald-600 border-gray-300 dark:border-gray-600 rounded focus:ring-emerald-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white/90">
                            Exportar por etiquetas
                          </span>
                          {selectedConnection && (() => {
                            const conn = connections.find((c) => c.id === selectedConnection);
                            if (conn?.provider !== 'WAHA') {
                              return (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                                  ⚠️ Apenas WhatsApp Business
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Exportar contatos filtrados por etiquetas do WhatsApp Business.
                        </p>
                      </div>
                    </label>

                    <button
                      type="button"
                      onClick={handleFetchData}
                      disabled={loading || !selectedConnection}
                      className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-transparent bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? '🔄 Buscando...' : '🔍 Buscar dados'}
                    </button>
                  </div>
                </div>

                {/* Coluna direita: etiquetas e grupos encontrados */}
                <div className="space-y-6">
                  {/* Etiquetas */}
                  {importByLabels && selectedConnection && (() => {
                    const conn = connections.find((c) => c.id === selectedConnection);
                    if (!conn) return null;

                    if (conn.provider !== 'WAHA') {
                      return (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                          <div className="flex items-start gap-3">
                            <span className="text-amber-600 text-lg">⚠️</span>
                            <div>
                              <p className="text-sm font-medium text-amber-800">Apenas WhatsApp Business</p>
                              <p className="text-xs text-amber-700 mt-1">
                                Exportar por etiquetas só funciona para conexões WhatsApp Business (WAHA). Selecione
                                uma conexão WAHA para usar essa opção.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (loadingLabels) {
                      return (
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Etiquetas</h3>
                          <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                            Carregando etiquetas...
                          </div>
                        </div>
                      );
                    }

                    if (whatsappLabels.length === 0) {
                      return (
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Etiquetas</h3>
                          <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                            Nenhuma etiqueta encontrada. Verifique se a conexão está ativa e possui etiquetas
                            configuradas no WhatsApp Business.
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-3">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                            Etiquetas encontradas ({whatsappLabels.length})
                          </h3>
                          <div className="mt-2 bg-blue-50 border border-blue-200 rounded-xl p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-blue-600 text-lg">ℹ️</span>
                              <div className="text-xs sm:text-sm text-blue-800">
                                <p className="font-medium mb-1">Sobre a privacidade dos contatos</p>
                                <p className="text-blue-700">
                                  Alguns contatos podem aparecer apenas com um ID em vez do número/nome completo.
                                  Você ainda consegue enviar mensagens normalmente usando esse ID.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {whatsappLabels.map((label) => (
                            <div
                              key={label.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <div>
                                  <div className="flex items-center gap-2">
                                    {label.colorHex && (
                                      <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: label.colorHex }}
                                      />
                                    )}
                                    <p className="font-medium text-gray-900">{label.name}</p>
                                    <span
                                      className="text-xs px-2 py-1 rounded-full font-medium"
                                      style={{
                                        backgroundColor: label.colorHex ? `${label.colorHex}20` : '#f3f4f6',
                                        color: label.colorHex || '#6b7280',
                                      }}
                                    >
                                      🏷️ Etiqueta
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {labelContactsCount[label.id] !== undefined
                                      ? `${labelContactsCount[label.id]} contatos adicionados`
                                      : 'Clique para buscar contatos desta etiqueta.'}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleFetchLabelContacts(label.id, label.name)}
                                className="btn-atualizar"
                              >
                                <i className="fas fa-magnifying-glass-plus" aria-hidden />
                                Buscar contatos
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Grupos */}
                  {groups.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                        Grupos encontrados ({groups.length})
                      </h3>
                      <div
                        className="space-y-2 overflow-y-auto"
                        style={groupsPanelHeight ? { maxHeight: groupsPanelHeight } : undefined}
                      >
                        {groups.map((group) => (
                          <div
                            key={group.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">{group.name}</p>
                                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                                    👥 Grupo
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {group.participantsCount} participantes
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleFetchGroupParticipants(group.id, group.name)}
                              className="btn-atualizar"
                            >
                              <i className="fas fa-user-plus" aria-hidden />
                              Buscar participantes
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contatos encontrados (seção dentro do mesmo card) */}
              {allImportedContacts.length > 0 && (
                <div className="pt-6 border-t border-gray-200 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                      3. Contatos encontrados ({allImportedContacts.length})
                    </h3>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleSelectAll(paginatedContacts)}
                        className="text-xs sm:text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                      >
                        {allSelectedOnPage ? '☐ Desmarcar página' : '☑ Selecionar página'}
                      </button>
                      <button
                        type="button"
                        onClick={handleSelectAllGlobal}
                        className="hidden sm:inline-flex text-[11px] text-gray-500 hover:text-emerald-700 underline underline-offset-2"
                      >
                        {selectedContacts.length === allImportedContacts.length
                          ? 'Limpar seleção geral'
                          : `Selecionar todos (${allImportedContacts.length})`}
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              checked={
                                paginatedContacts.length > 0 && allSelectedOnPage
                              }
                              onChange={() => handleSelectAll(paginatedContacts)}
                              className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contato
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Telefone / ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Origem
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedContacts.map((contact) => (
                          <tr
                            key={contact.id}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => handleToggleContact(contact.id)}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedContacts.includes(contact.id)}
                                onChange={() => handleToggleContact(contact.id)}
                                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contact.name && !contact.name.match(/^\d+$/) ? (
                                contact.name
                              ) : (
                                <span className="text-gray-500">
                                  Contato (ID: {contact.phone?.substring(0, 10)}…)
                                </span>
                              )}
                              {contact.groupName && contact.source !== 'label' && (
                                <div className="text-xs text-gray-500 mt-0.5">👥 {contact.groupName}</div>
                              )}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                              {contact.phone && !contact.phone.match(/^\d{15,}$/) ? (
                                contact.phone
                              ) : (
                                <span className="text-gray-500 text-xs">
                                  ID privado • Você pode enviar mensagens normalmente
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm">
                              {contact.source === 'label' && contact.groupName ? (
                                <span
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: contact.labelColor ? `${contact.labelColor}20` : '#f3e8ff',
                                    color: contact.labelColor || '#7c3aed',
                                  }}
                                >
                                  {contact.labelColor && (
                                    <span
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: contact.labelColor }}
                                    />
                                  )}
                                  {contact.groupName.replace('🏷️ ', '')}
                                </span>
                              ) : (
                                <span className="inline-flex px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                                  {contact.source === 'contact'
                                    ? '📇 Contato'
                                    : contact.source === 'group'
                                    ? '👥 Grupo'
                                    : contact.source === 'label'
                                    ? '🏷️ Etiqueta'
                                    : '💬 Chat'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-500">
                    <span>
                      Mostrando{' '}
                      <span className="font-medium">
                        {startIndex + 1}–{startIndex + paginatedContacts.length}
                      </span>{' '}
                      de <span className="font-medium">{totalContacts}</span> contatos
                    </span>
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPageSafe === 1}
                        className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="fas fa-chevron-left mr-1 text-xs" aria-hidden />
                        Anterior
                      </button>
                      <span className="text-[11px]">
                        Página{' '}
                        <span className="font-semibold">
                          {currentPageSafe}/{totalPages}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPageSafe === totalPages}
                        className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima
                        <i className="fas fa-chevron-right ml-1 text-xs" aria-hidden />
                      </button>
                    </div>
                  </div>

                  {/* Botões no rodapé, alinhados à direita */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setAllImportedContacts([]);
                        setSelectedContacts([]);
                        toast.success('Lista de contatos limpa!');
                      }}
                      disabled={allImportedContacts.length === 0}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2.5 text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-trash-alt text-base shrink-0" aria-hidden />
                      Limpar contatos
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCSV}
                      disabled={selectedContacts.length === 0}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-file-csv text-base shrink-0" aria-hidden />
                      Exportar CSV ({selectedContacts.length})
                    </button>
                    <button
                      type="button"
                      onClick={handleImportClick}
                      disabled={selectedContacts.length === 0}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-transparent bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-address-book text-base shrink-0" aria-hidden />
                      Importar para o sistema ({selectedContacts.length})
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Modal de seleção de categoria */}
          {showImportModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="card-padrao max-w-md w-full mx-4 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Selecionar categoria</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Escolha em qual categoria deseja importar os {selectedContacts.length} contatos selecionados:
                </p>

                <div className="mb-6">
                  <ScrollableSelect
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    placeholder="Sem categoria"
                    options={importCategoryOptions}
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmImport}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-transparent bg-emerald-600 text-sm font-medium text-white hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                  >
                    Confirmar importação
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </PageLayout>
    </>
  );
}
